/**
 * Pure helpers for the declarative customer skin renderer.
 *
 * Everything here works from the resolved effective skin returned by the API;
 * no organisation names, ids, brand colours or guide measurements live in
 * renderer code. Colours, typography, logo presentation and decorative
 * geometry all arrive as configuration.
 */
import type {
  CustomerSkinEffective,
  CustomerSkinPalette,
  CustomerSkinTheme,
  CustomerSkinTypography
} from '@cdf/api-client-react';

export type SkinThemeName = 'light' | 'dark';

const hexPattern = /^#([0-9a-fA-F]{6})$/;

/** sRGB channels (0-255) for a six-digit hex colour; null when unparseable. */
export function hexChannels(value: string | null | undefined): { r: number; g: number; b: number } | null {
  if (!value) return null;
  const match = hexPattern.exec(value.trim());
  if (!match) return null;
  const parsed = Number.parseInt(match[1] ?? '', 16);
  if (!Number.isFinite(parsed)) return null;
  return { r: (parsed >> 16) & 0xff, g: (parsed >> 8) & 0xff, b: parsed & 0xff };
}

function channelLuminance(channel: number): number {
  const component = channel / 255;
  return component <= 0.04045 ? component / 12.92 : ((component + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance for a six-digit hex colour; null when unparseable. */
export function colourLuminance(value: string | null | undefined): number | null {
  const channels = hexChannels(value);
  if (!channels) return null;
  return (
    channelLuminance(channels.r) * 0.2126 +
    channelLuminance(channels.g) * 0.7152 +
    channelLuminance(channels.b) * 0.0722
  );
}

/** WCAG contrast ratio between two six-digit hex colours; null when either is unparseable. */
export function contrastRatio(a: string | null | undefined, b: string | null | undefined): number | null {
  const first = colourLuminance(a);
  const second = colourLuminance(b);
  if (first === null || second === null) return null;
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

/** White or black, whichever reads best on the supplied colour; null when unparseable. */
export function readableForeground(value: string): string | null {
  const luminance = colourLuminance(value);
  if (luminance === null) return null;
  const white = 1.05 / (luminance + 0.05);
  const black = (luminance + 0.05) / 0.05;
  return white >= black ? '#ffffff' : '#000000';
}

/** True when a surface is dark enough that white text is the readable choice. */
export function isDarkSurface(value: string | null | undefined): boolean {
  const luminance = colourLuminance(value);
  if (luminance === null) return false;
  return 1.05 / (luminance + 0.05) >= 4.5;
}

/** Active theme values for the resolved skin. */
export function skinTheme(skin: CustomerSkinEffective | null, theme: SkinThemeName): CustomerSkinTheme {
  if (!skin) return {};
  return (theme === 'dark' ? skin.dark : skin.light) ?? {};
}

/** Bundled font stack for the configured typography; null keeps the app default. */
export function skinFontStack(typography: CustomerSkinTypography | null | undefined): string | null {
  if (typography?.font !== 'lato') return null;
  const fallback =
    typography.fallback === 'system'
      ? 'ui-sans-serif, system-ui, sans-serif'
      : "Calibri, 'Segoe UI', ui-sans-serif, system-ui, sans-serif";
  return `'Lato', ${fallback}`;
}

export interface SkinTokenInput {
  theme: SkinThemeName;
  themeValues: CustomerSkinTheme;
  palette: CustomerSkinPalette;
  header: { background: string; foreground: string } | null;
  fontFamily: string | null;
}

/**
 * Semantic tokens for the document root. The resolved skin wins; the legacy
 * primary pair stays as fallback so customers without a skin keep their
 * current appearance.
 */
export function skinTokens(
  input: SkinTokenInput,
  legacy: { primary?: string | null; foreground?: string | null } = {}
): Record<string, string> {
  const tokens: Record<string, string> = {};
  const primary = input.themeValues.primary ?? legacy.primary ?? null;
  const foreground =
    input.themeValues.onPrimary ??
    (input.themeValues.primary ? readableForeground(input.themeValues.primary) : null) ??
    legacy.foreground ??
    null;
  if (primary) {
    tokens['--primary'] = primary;
    tokens['--ring'] = primary;
  }
  if (foreground) {
    tokens['--primary-foreground'] = foreground;
  }
  if (input.header) {
    tokens['--customer-header-bg'] = input.header.background;
    tokens['--customer-header-fg'] = input.header.foreground;
  }
  if (input.themeValues.accent) {
    tokens['--customer-accent'] = input.themeValues.accent;
  }
  for (const entry of paletteEntries(input.palette)) {
    tokens[`--customer-palette-${entry.key}`] = entry.value;
  }
  if (input.fontFamily) {
    tokens['--customer-font-family'] = input.fontFamily;
  }
  return tokens;
}

/** Palette entries that carry a usable value, in declaration order. */
export function paletteEntries(
  palette: CustomerSkinPalette | null | undefined
): Array<{ key: keyof CustomerSkinPalette; value: string }> {
  if (!palette) return [];
  const entries: Array<{ key: keyof CustomerSkinPalette; value: string }> = [];
  for (const [key, value] of Object.entries(palette)) {
    if (typeof value === 'string' && value.length > 0) {
      entries.push({ key: key as keyof CustomerSkinPalette, value });
    }
  }
  return entries;
}

export interface CircleLayout {
  width: number;
  height: number;
  radius: number;
  centres: Array<{ x: number; y: number }>;
}

/** Base radius for the decorative circle device, before configuration. */
export const CIRCLE_BASE_RADIUS = 56;

/**
 * Lay out overlapping circles for the decorative device.
 *
 * `overlap` (0-100) is how much of their diameter adjacent circles share and
 * `crop` (0-80) hides that share of the circle height from the bottom, so the
 * motif keeps its thicker lower stroke while losing no more than the schema
 * allows.
 */
export function circleLayout(count: number, overlap = 50, crop = 0, radius = CIRCLE_BASE_RADIUS): CircleLayout {
  const circles = Math.min(4, Math.max(1, Math.round(count)));
  const overlapRatio = Math.min(100, Math.max(0, overlap)) / 100;
  const cropRatio = Math.min(80, Math.max(0, crop)) / 100;
  const step = 2 * radius * (1 - overlapRatio);
  const centres = Array.from({ length: circles }, (_, index) => ({
    x: radius + index * step,
    y: radius
  }));
  return {
    width: step * (circles - 1) + 2 * radius,
    height: 2 * radius * (1 - cropRatio),
    radius,
    centres
  };
}
